/**
 * Canonical item relationship types.
 *
 * Phase 1 note:
 * - Additive only
 * - Existing lightweight relation and clustering types remain for compatibility
 * - New code should prefer these types over extending legacy relationship models
 */

export type RelationshipStatus =
  | 'candidate'
  | 'confirmed'
  | 'dismissed'
  | 'active'
  | 'dissolved';

export type RelationshipDeclaredMode = 'declared' | 'emergent';

export type RelationshipGroupType =
  | 'cluster'
  | 'package'
  | 'exclusive'
  | 'related_group'
  | 'collection';

/**
 * Collection subtype distinguishes the two use modes:
 * - 'pool': type-B — a bounded scope that defines the universe for a prioritization exercise.
 *   Activating a pool scope triggers scope completeness checks (partial packages, external deps, etc.).
 * - 'curation': type-A — a named human-curated list for reporting or stakeholder sharing.
 *   No enforcement weight; does not trigger scope completeness checks.
 */
export type CollectionSubtype = 'pool' | 'curation';

export type RelationshipEdgeType =
  | 'related'
  | 'dependency'
  | 'prerequisite'
  | 'aggregation'
  | 'elaboration'
  | 'breakdown'
  | 'contains'
  | 'reframe'
  | 'carries';

/**
 * The four canonical categories that organise the relationship types.
 *
 * Horizontal  — peer-level structural relationships (Cluster, Package, Competing, Dependency, Prerequisite, Carries)
 * Vertical    — hierarchical relationships (Aggregation, Breakdown, Contains, Elaboration)
 * Curatorial  — lightweight thematic grouping with no governance weight (Collection)
 * Framing     — same item through a different lens (Reframe)
 */
export type RelationshipCategory = 'horizontal' | 'vertical' | 'curatorial' | 'framing';

/**
 * Maps every canonical type to its category.
 * Edge types and group types share the same category space.
 */
export const RELATIONSHIP_CATEGORY_MAP: Record<string, RelationshipCategory> = {
  // Horizontal (edge types)
  dependency:   'horizontal',
  prerequisite: 'horizontal',
  carries:      'horizontal', // canon 12 §5.11 — explains a rank; no selection or order weight
  // Horizontal (group types)
  cluster:      'horizontal',
  package:      'horizontal',
  exclusive:    'horizontal', // product label: "Competing"
  // Vertical (edge types)
  aggregation:  'vertical',
  breakdown:    'vertical',
  contains:     'vertical',
  elaboration:  'vertical',
  // Curatorial (group type)
  collection:   'curatorial',
  // Framing (edge type)
  reframe:      'framing',
  // Legacy
  related:      'horizontal',
  related_group: 'horizontal',
};

/**
 * The five provenance / lineage action types.
 *
 * These record how items changed identity over time. Stored in `lineage_events`.
 *
 * - retire    : item is permanently closed, no successor
 * - supersede : item A replaced by item B; A is closed
 * - split     : item A became two or more items; A is closed
 * - merge     : two or more items became one; sources are closed
 * - fork      : item A spawned item B as a variant; A continues to exist
 */
export type ItemProvenanceActionType = 'retire' | 'supersede' | 'split' | 'merge' | 'fork';

export interface ItemProvenanceAction {
  id: string;
  eventType: ItemProvenanceActionType;
  sourceItemIds: string[];
  targetItemIds: string[];
  performedBy?: string;
  rationale?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
}

export type RelationshipDirectionality = 'directed' | 'undirected';

export type ClusterSubtype = 'duplicate' | 'variant';

/**
 * Dependency / Prerequisite strength.
 *
 * - 'informational': context only, no gate
 * - 'soft': advisory; selecting one without the other may reduce value/coherence
 * - 'hard': must be addressed before a commitment-quality outcome is legitimate
 *   (blocks at commitment time)
 * - 'gate': candidacy / scope-entry gate. The dependent item is not a valid
 *   candidate in a prioritization pool unless the gate target is also in that
 *   pool ("cannot enter scope until the gate item exists"). Stronger than 'hard':
 *   it blocks at inclusion time, not only at commitment. Evaluated against the
 *   active scope (canon 07 §5.4; gate 3 in canon 07A §3).
 */
export type DependencySubtype = 'informational' | 'soft' | 'hard' | 'gate';

export type PackageSubtype = 'prioritize' | 'select' | 'prioritize_and_select';

export type ElaborationSubtype = 'solution_spectrum' | 'solution_variant';

/**
 * Exclusive (product label: "Competing") models peer-level mutual exclusivity:
 * "one or the other, not both." At most one member of an exclusive group may
 * receive a commitment-quality disposition. Unlike Package (all-or-nothing) and
 * Elaboration (parent need → alternative children), Exclusive is a peer set with
 * no parent — the members compete for the same commitment slot.
 *
 * Subtypes capture *why* the members are exclusive:
 * - 'competing_alternative': they address the same need / draw on the same
 *   budget or slot; committing to one makes the others redundant (value drops).
 * - 'incompatible': they cannot coexist for logical/technical reasons; committing
 *   to both is structurally invalid, not merely wasteful.
 */
export type ExclusiveSubtype = 'competing_alternative' | 'incompatible';

export type GroupSubtype = ClusterSubtype | PackageSubtype | ExclusiveSubtype | CollectionSubtype;

export interface ItemRelationshipGroup {
  id: string;
  type: RelationshipGroupType;
  subtype?: GroupSubtype;
  status: RelationshipStatus;
  declaredMode: RelationshipDeclaredMode;
  title?: string;
  note?: string;
  rationale?: string;
  createdAt: Date;
  createdBy: string;
  reviewedAt?: Date;
  reviewedBy?: string;
  workspaceId?: string;
}

export interface ItemRelationshipGroupRow {
  id: string;
  workspace_id?: string | null;
  group_type: RelationshipGroupType;
  subtype?: GroupSubtype | null;
  status: RelationshipStatus;
  declared_mode: RelationshipDeclaredMode;
  title?: string | null;
  note?: string | null;
  rationale?: string | null;
  created_at: string;
  created_by?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
}

export interface ItemRelationshipGroupMembership {
  id: string;
  groupId: string;
  itemId: string;
  membershipRole?: 'canonical' | 'member' | 'optional';
  addedAt: Date;
  addedBy?: string;
  inclusionReason?: string;
  workspaceId?: string;
}

export interface ItemRelationshipGroupMembershipRow {
  id: string;
  group_id: string;
  item_id: string;
  membership_role?: 'canonical' | 'member' | 'optional' | null;
  added_at: string;
  added_by?: string | null;
  inclusion_reason?: string | null;
  workspace_id?: string | null;
}

/**
 * Metadata key under which an elaboration edge stores the parent frame types it
 * applies to (canon 07 Rule 24 resolution — frame-contextualized elaboration).
 *
 * An empty / absent scope means the elaboration child is a valid possibility under
 * *every* framing of the parent (the universal, backward-compatible default).
 * A non-empty scope means the child only contributes to the option set when the
 * parent's active frame type is one of the listed types.
 */
export const ELABORATION_FRAME_SCOPE_KEY = 'frameScope';

/** Read the parent-frame-type scope from an elaboration edge's metadata. */
export function getElaborationFrameScope(
  edge: { metadata?: Record<string, unknown> | null },
): string[] {
  const raw = edge.metadata?.[ELABORATION_FRAME_SCOPE_KEY];
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
}

/**
 * Whether an elaboration edge applies under the parent's active frame type.
 * Universal (empty scope) edges always apply. A scoped edge applies only when
 * the active frame type matches; when no frame is declared, a scoped edge is
 * treated as not-yet-determinable (excluded) — Gate 2 handles the missing-frame
 * declaration for multi-framed items.
 */
export function elaborationEdgeAppliesToFrame(
  edge: { metadata?: Record<string, unknown> | null },
  activeFrameType: string | null,
): boolean {
  const scope = getElaborationFrameScope(edge);
  if (scope.length === 0) return true;
  if (!activeFrameType) return false;
  return scope.includes(activeFrameType);
}

export interface ItemRelationshipEdge {
  id: string;
  type: RelationshipEdgeType;
  subtype?: DependencySubtype | ElaborationSubtype;
  directionality: RelationshipDirectionality;
  status: RelationshipStatus;
  declaredMode: RelationshipDeclaredMode;
  sourceItemId: string;
  targetItemId: string;
  label?: string;
  rationale?: string;
  /**
   * Carries only (canon 12 §5.11): the share of the target's worth the source
   * carries, as a fraction above 0 and at most 1 (0.4 = 40%). Required on a
   * carries edge, absent on every other type. See src/lib/carriesRelationships.ts.
   */
  magnitude?: number;
  metadata?: Record<string, unknown>;
  createdAt: Date;
  createdBy?: string;
  reviewedAt?: Date;
  reviewedBy?: string;
  workspaceId?: string;
}

export interface ItemRelationshipEdgeRow {
  id: string;
  workspace_id?: string | null;
  relationship_type: RelationshipEdgeType;
  subtype?: DependencySubtype | ElaborationSubtype | null;
  directionality: RelationshipDirectionality;
  status: RelationshipStatus;
  declared_mode: RelationshipDeclaredMode;
  source_item_id: string;
  target_item_id: string;
  label?: string | null;
  rationale?: string | null;
  /** numeric in Postgres — PostgREST may return it as a string. */
  magnitude?: number | string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  created_by?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
}

export interface ItemMutationReview {
  id: string;
  itemId: string;
  workspaceId?: string;
  cycleId?: string;
  trackId?: string;
  sessionId?: string;
  ringAtTime: 1 | 2 | 3;
  mutationType: string;
  affectedFields: string[];
  requestedBy?: string;
  requestedAt: Date;
  approvalLevelRequired: 'standard' | 'intermediate' | 'admin';
  approvedBy?: string;
  approvedAt?: Date;
  decision: 'approved' | 'rejected' | 'deferred';
  sessionDisposition?: 'continue' | 'restart' | 'defer_until_session_close';
  reason?: string;
}

export interface ItemMutationReviewRow {
  id: string;
  item_id: string;
  workspace_id?: string | null;
  cycle_id?: string | null;
  track_id?: string | null;
  session_id?: string | null;
  ring_at_time: 1 | 2 | 3;
  mutation_type: string;
  affected_fields: string[] | null;
  requested_by?: string | null;
  requested_at: string;
  approval_level_required: 'standard' | 'intermediate' | 'admin';
  approved_by?: string | null;
  approved_at?: string | null;
  decision: 'approved' | 'rejected' | 'deferred';
  session_disposition?: 'continue' | 'restart' | 'defer_until_session_close' | null;
  reason?: string | null;
}

export interface ItemClosureEvent {
  id: string;
  itemId: string;
  workspaceId?: string;
  closureType:
    | 'reframed_to_canonical'
    | 'duplicate_closed'
    | 'superseded_closed'
    | 'retired_closed'
    | 'other';
  pointerItemId?: string;
  rationale?: string;
  createdAt: Date;
  createdBy?: string;
  metadata?: Record<string, unknown>;
}

export interface ItemClosureEventRow {
  id: string;
  item_id: string;
  workspace_id?: string | null;
  closure_type: 'reframed_to_canonical' | 'duplicate_closed' | 'superseded_closed' | 'retired_closed' | 'other';
  pointer_item_id?: string | null;
  rationale?: string | null;
  created_at: string;
  created_by?: string | null;
  metadata?: Record<string, unknown> | null;
}

export function itemRelationshipGroupRowToDomain(row: ItemRelationshipGroupRow): ItemRelationshipGroup {
  return {
    id: row.id,
    type: row.group_type,
    subtype: row.subtype ?? undefined,
    status: row.status,
    declaredMode: row.declared_mode,
    title: row.title ?? undefined,
    note: row.note ?? undefined,
    rationale: row.rationale ?? undefined,
    createdAt: new Date(row.created_at),
    createdBy: row.created_by ?? '',
    reviewedAt: row.reviewed_at ? new Date(row.reviewed_at) : undefined,
    reviewedBy: row.reviewed_by ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
  };
}

export function itemRelationshipGroupMembershipRowToDomain(
  row: ItemRelationshipGroupMembershipRow,
): ItemRelationshipGroupMembership {
  return {
    id: row.id,
    groupId: row.group_id,
    itemId: row.item_id,
    membershipRole: row.membership_role ?? undefined,
    addedAt: new Date(row.added_at),
    addedBy: row.added_by ?? undefined,
    inclusionReason: row.inclusion_reason ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
  };
}

export function itemRelationshipEdgeRowToDomain(row: ItemRelationshipEdgeRow): ItemRelationshipEdge {
  return {
    id: row.id,
    type: row.relationship_type,
    subtype: row.subtype ?? undefined,
    directionality: row.directionality,
    status: row.status,
    declaredMode: row.declared_mode,
    sourceItemId: row.source_item_id,
    targetItemId: row.target_item_id,
    label: row.label ?? undefined,
    rationale: row.rationale ?? undefined,
    magnitude: row.magnitude == null || row.magnitude === '' ? undefined : Number(row.magnitude),
    metadata: row.metadata ?? undefined,
    createdAt: new Date(row.created_at),
    createdBy: row.created_by ?? undefined,
    reviewedAt: row.reviewed_at ? new Date(row.reviewed_at) : undefined,
    reviewedBy: row.reviewed_by ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
  };
}

export function itemMutationReviewRowToDomain(row: ItemMutationReviewRow): ItemMutationReview {
  return {
    id: row.id,
    itemId: row.item_id,
    workspaceId: row.workspace_id ?? undefined,
    cycleId: row.cycle_id ?? undefined,
    trackId: row.track_id ?? undefined,
    sessionId: row.session_id ?? undefined,
    ringAtTime: row.ring_at_time,
    mutationType: row.mutation_type,
    affectedFields: row.affected_fields ?? [],
    requestedBy: row.requested_by ?? undefined,
    requestedAt: new Date(row.requested_at),
    approvalLevelRequired: row.approval_level_required,
    approvedBy: row.approved_by ?? undefined,
    approvedAt: row.approved_at ? new Date(row.approved_at) : undefined,
    decision: row.decision,
    sessionDisposition: row.session_disposition ?? undefined,
    reason: row.reason ?? undefined,
  };
}

export function itemClosureEventRowToDomain(row: ItemClosureEventRow): ItemClosureEvent {
  return {
    id: row.id,
    itemId: row.item_id,
    workspaceId: row.workspace_id ?? undefined,
    closureType: row.closure_type,
    pointerItemId: row.pointer_item_id ?? undefined,
    rationale: row.rationale ?? undefined,
    createdAt: new Date(row.created_at),
    createdBy: row.created_by ?? undefined,
    metadata: row.metadata ?? undefined,
  };
}
